import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography
} from '@mui/material';
import {Search} from '@mui/icons-material';
import {useListContext} from 'components/list/ListContext';
import {useQuery} from '@apollo/client/react';
import {GET_EVENTS_STATUS_COUNTS} from 'services/gqlQueries';
import {useSearchParams} from 'react-router-dom';

const STATUS_TABS = [
  {id: 'all', label: 'All', countKey: 'all'},
  {id: 'live', label: 'Live', countKey: 'live'},
  {id: 'upcoming', label: 'Upcoming', countKey: 'upcoming'},
  {id: 'finished', label: 'Finished', countKey: 'finished'}
] as const;

const CATEGORIES = [
  {id: 'all', label: 'All categories'},
  {id: 'orienteering', label: 'Orienteering'},
  {id: 'running', label: 'Running'},
  {id: 'test', label: 'Test'}
];

const EventsListFilters = () => {
  const {filters, addFilters, total} = useListContext();
  const setFilters = addFilters ?? (() => undefined);
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('q') || '';
  const [search, setSearch] = React.useState(initialSearch);

  const {data} = useQuery<{
    _getEventsStatusCounts: {all: number; live: number; upcoming: number; finished: number};
  }>(GET_EVENTS_STATUS_COUNTS);

  const counts = data?._getEventsStatusCounts;

  React.useEffect(() => {
    if (initialSearch) {
      setFilters({search: initialSearch});
    }
  }, [initialSearch, setFilters]);

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      setFilters({search});
    }, 250);
    return () => window.clearTimeout(handle);
  }, [search, setFilters]);

  const activeStatus = filters?.status || 'all';

  return (
    <Stack spacing={2} sx={{mb: 2, mt: 1}}>
      {typeof total === 'number' && (
        <Typography variant="body2" color="text.secondary">
          {total} events
        </Typography>
      )}
      <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5} alignItems={{md: 'center'}}>
        <OutlinedInput
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search events..."
          startAdornment={
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          }
          sx={{flex: 1, minWidth: {xs: '100%', md: 240}, backgroundColor: 'background.paper'}}
        />
        <FormControl size="small" sx={{minWidth: {xs: '100%', md: 160}, backgroundColor: 'background.paper'}}>
          <InputLabel>Category</InputLabel>
          <Select
            label="Category"
            value={filters?.category || 'all'}
            onChange={(e) => setFilters({category: e.target.value})}
          >
            {CATEGORIES.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
        {STATUS_TABS.map((tab) => {
          const selected = activeStatus === tab.id;
          const count = counts?.[tab.countKey];
          return (
            <Chip
              key={tab.id}
              clickable
              label={`${tab.label}${typeof count === 'number' ? ` ${count}` : ''}`}
              onClick={() => setFilters({status: tab.id})}
              sx={{
                height: 36,
                fontWeight: selected ? 700 : 500,
                borderRadius: 2,
                backgroundColor: selected ? 'rgba(255, 0, 102, 0.12)' : 'background.paper',
                color: selected ? 'primary.main' : 'text.primary',
                border: selected ? '1px solid rgba(255, 0, 102, 0.4)' : '1px solid',
                borderColor: selected ? 'rgba(255, 0, 102, 0.4)' : 'divider'
              }}
            />
          );
        })}
      </Box>
    </Stack>
  );
};

export default EventsListFilters;
